/**
 * AI-snippet funnel copy for the database/chart tools.
 *
 * Each entry is written for the open-loop pattern documented in
 * `src/components/SnippetFunnel.tsx`:
 *
 *  - `question` — phrased the way people search it.
 *  - `answer` — a complete, quotable explanation of the *method*: what the
 *    number is, how it is derived, what changes it. Deliberately contains no
 *    per-Pal or per-card figure, so an answer box can cite it accurately but
 *    cannot substitute for the table.
 *  - `withheld` — the concrete rows/values that only resolve on the page.
 *  - `cta` — where those values live.
 *
 * Kept in one module so the copy stays consistent and can be reviewed in a
 * single place; the route and its lazily-loaded content component both read
 * the same entry without importing each other.
 */
export interface SnippetCopy {
  question: string;
  answer: string;
  withheld: string[];
  cta: { to: string; label: string };
}

export const SNIPPETS: Record<string, SnippetCopy> = {
  breeding: {
    question: "How does breeding work in Palworld?",
    answer:
      "Palworld breeding averages the hidden breed power of the two parents and hatches the Pal whose own breed power sits closest to that average, which is why a given pair always produces the same species regardless of level or which parent is which. Passive skills are inherited separately from the parents' pools, so the pairing decides the species while the passives decide the quality. Working out a specific child, or every parent pair that produces one Pal, needs the full breed-power table for the whole roster.",
    withheld: [
      "The exact child for any pair you select, both directions",
      "Every parent combination that hatches a chosen Pal",
      "Breed-power values behind each pairing",
      "Unique pair overrides that ignore the average",
      "Element and work suitability of each result",
      "Links straight into the passive-inheritance guide",
    ],
    cta: { to: "/breeding", label: "Open the breeding calculator" },
  },
  damage: {
    question: "How is Palworld damage calculated?",
    answer:
      "A Pal's output in Palworld is base attack multiplied by its element bonus and its attack cadence, giving effective DPS; the element matchup then multiplies the hit again — roughly 1.5x into a weakness and 0.5x into a resistance — before passive skills, condenser rank and the world damage multiplier are applied. Because a weakness multiplier outweighs raw attack, the best Pal for a fight depends on the target rather than on a fixed ranking, so the answer is always per-boss.",
    withheld: [
      "Base attack, HP and defence for every Pal",
      "Effective DPS ranking, sortable",
      "Per-boss weakness and resistance multipliers",
      "Estimated solo time-to-kill against each boss",
      "Element bonus applied per Pal",
      "Re-ranked results after you pick a boss target",
    ],
    cta: { to: "/damage", label: "Open the damage calculator" },
  },
  mounts: {
    question: "How are Palworld mount speeds compared?",
    answer:
      "Every rideable Pal in Palworld carries separate ground, flying and swimming speed values, and a mount only uses the value for the movement type it is capable of — which is why a fast ground mount can be slower than a mid-tier flyer over the same route. Ranking mounts therefore means comparing within a category, then weighing stamina drain and how early the saddle unlocks. Choosing one needs the per-Pal speed figures side by side.",
    withheld: [
      "Fly, ground and swim speed for every rideable Pal",
      "Mount category filter with live re-sorting",
      "Fastest flyer, runner and swimmer at a glance",
      "Saddle requirement per mount",
      "Element and portrait for each mount",
      "Full rideable roster, not a top-five list",
    ],
    cta: { to: "/mounts", label: "Open the mount speed table" },
  },
  capture: {
    question: "How do Palworld capture rates work?",
    answer:
      "A capture attempt in Palworld resolves as base catch rate multiplied by the sphere tier, the target's remaining HP factor and a bonus for throwing from behind unnoticed. Base rate is a fixed per-Pal value, so weakening a Pal and moving up one sphere tier are the two levers you control; alphas and legendaries keep a low base rate no matter how many spheres you throw. Planning a capture run needs the base rate and recommended sphere tier for the specific Pal.",
    withheld: [
      "Base catch rate for every Pal",
      "Recommended sphere tier per Pal",
      "Searchable, sortable full roster table",
      "Element and habitat context per row",
      "Daily food cost per Pal",
      "Which Pals need a multi-attempt plan",
    ],
    cta: { to: "/capture-rates", label: "Open the capture rate table" },
  },
  teams: {
    question: "How do you build a good team in Palworld?",
    answer:
      "A Palworld party holds five Pals, so a working squad is judged on coverage rather than individual power: enough attack elements that no boss resists your whole team, at least one mount for the terrain you are crossing, and the work suitabilities your base or expedition actually needs. Because each slot competes with the others, the useful check is what a specific five-Pal combination covers and what it leaves exposed.",
    withheld: [
      "Element, work and mount coverage for your own five picks",
      "Gaps flagged as you swap Pals in and out",
      "Ready-made PvE, PvP, boss, base and farming comps",
      "Per-team purpose notes and stat totals",
      "A shareable link for the squad you built",
      "Every Pal in the picker, not a curated shortlist",
    ],
    cta: { to: "/team-builder", label: "Open the team builder" },
  },
  elements: {
    question: "How does the Palworld type chart work?",
    answer:
      "Palworld uses nine elements, and each attacking element is strong into exactly one defending element and resisted by one other, with everything else resolving as neutral damage; Neutral itself has no advantages at all. That means no element is strongest overall — the chart is used to pick a counter per fight, and the final hit is the base damage multiplied by the matchup, then by a critical hit and reduced by the defender's resistance.",
    withheld: [
      "Every attack-versus-defend multiplier in one grid",
      "Live final-hit calculation for your own numbers",
      "Critical hit and resistance sliders",
      "Damage-over-time tick estimate",
      "Colour-coded strong and resisted cells",
      "All nine elements, both directions",
    ],
    cta: { to: "/elements", label: "Open the type chart" },
  },
  bulkOrder: {
    question: "How do you bulk order Palworld TCG cards?",
    answer:
      "A Palworld TCG bulk order is built as a quantity-plus-card-number list, which is then handed to TCGplayer's Mass Entry checkout so every single is added to one cart instead of being searched for individually. Because printed Palworld codes carry the leading E and parallels share a base number, the list has to be matched against the official printing list before it is submitted, otherwise a rarity or set mismatch silently drops lines. Live per-card cost only resolves after the list is matched and priced.",
    withheld: [
      "Card-name and set match for every code you paste",
      "CSV or text import of an existing deck list",
      "Full order preview with quantities before you buy",
      "Unmatched or mistyped lines flagged individually",
      "Database search to add cards you only know by name",
      "One-click hand-off to a single TCGplayer checkout",
    ],
    cta: { to: "/tcg-bulk-order", label: "Open the bulk order builder" },
  },
};
